import { LitElement, html } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { ref } from 'lit/directives/ref.js';

import { LIT_CSS_VARIABLES } from '@Root/lib/lit-styles.js';
import {
  SHARED_CLASSES,
  SHARED_ELEMENTS,
} from '@Root/lib/lit-3form-block/styles.js';

import { imagebossUrl } from '@Helpers/imageboss';
import { debounce } from '@Helpers/debounce';

import { styles, gridClassMiscDriverSuffix } from './css';
import { isDealerPage } from '../../helpers/helpers';
import { handleAdobeNavEvent } from '../../helpers/adobe-analytics';

const gridClassOptions = [
    'one-segment',
    'two-segments',
    'three-segments',
    'four-segments',
    'five-segments',
    'six-segments',
  ],
  resizeDebounceMilliseconds = 1000.0 / 60;

const topNav = document.querySelector('.main-nav nav');

class NavPanel extends LitElement {
  static properties = {
    gridClasses: {},
    currentpanel: {
      state: true,
      type: String,
    },
    _mobileNavSlid: { type: Boolean },
    _panelShowing: { type: Boolean },
  };

  constructor() {
    super();
    this.setWindowSizeValues();
    this.currentpanel = this.getAttribute('currentpanel');
    this.gridClasses = { segments: true };
    this._mobileNavSlid = false;
    this._panelShowing = false;
    this.addEventListener('nav-item-selected', e => this.navItemSelected(e));
    this.addEventListener('reset-mobile-nav', e =>
      this.navItemSelected(e, true)
    );
    this.setGridClassName();
  }

  navItemSelected(e, useMainPanel) {
    this.currentpanel = useMainPanel
      ? ''
      : e.target.getAttribute('currentpanel');

    if (this.currentpanel === '') this._mobileNavSlid = false;
    this.setGridClassName();
    this._panelShowing = document
      .querySelector('nav-panel')
      .classList.contains('show');
    this.requestUpdate();

    // Not sure why LitElement doesn't recognize this.
    for (const cta of this.renderRoot.querySelectorAll('cta-button')) {
      cta.requestUpdate();
    }
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener(
      'resize',
      debounce(this._handleResize, resizeDebounceMilliseconds)
    );
  }

  setWindowSizeValues() {
    const vw = Math.max(
        document.documentElement.clientWidth || 0,
        window.innerWidth || 0
      ),
      currentValues = [
        window.useMobileLayout,
        window.useTabletLayout,
      ].toString();

    window.useMobileLayout = vw <= 600;
    window.useTabletLayout = 600 < vw && vw < 1024;

    const newValues = [
      window.useMobileLayout,
      window.useTabletLayout,
    ].toString();
    if (currentValues !== newValues) {
      const layoutShiftRequired = new Event('layout-shift-required', {
        bubbles: true,
        composed: true,
      });
      document.querySelector('nav-panel').dispatchEvent(layoutShiftRequired);
    }
  }

  disconnectedCallback() {
    window.removeEventListener(
      'resize',
      debounce(this._handleResize, resizeDebounceMilliseconds)
    );
    super.disconnectedCallback();
  }

  static get styles() {
    return [LIT_CSS_VARIABLES, SHARED_ELEMENTS, SHARED_CLASSES, styles];
  }

  render() {
    return this.useMobileLayout ? this.mobileHTML : this.desktopHTML;
  }

  get useMobileLayout() {
    // see site.js desktopWindowSize
    return window.useMobileLayout || window.useTabletLayout;
  }

  get desktopHTML() {
    return html`
      <section class="nav-wrapper">
        <div class="nav-grid">
          ${this.hasSegments
            ? html`
                <div class=${classMap(this.gridClasses)}>
                  ${this.segments.map(segment => this.segmentHtml(segment))}
                </div>
              `
            : ''}
          ${this.hasMiscDrivers
            ? html`
                <div class="misc-driver-vertical-rule"></div>
                <div class="misc-drivers">
                  ${this.hasMiscDrivers
                    ? html`
                        ${this.miscDrivers.map(
                          driverSection => html`
                            <div class="driver-section">
                              <p class="driver-section-title">
                                ${driverSection.title}
                              </p>
                              ${driverSection.drivers.map(
                                driver => html`
                                  <a
                                    @click="${event => {
                                      handleAdobeNavEvent({
                                        navString: driver.analyticsName,
                                        destinationUrl: driver.url,
                                        event,
                                      });
                                    }}"
                                    href="${driver.url}"
                                  >
                                    <p>
                                      ${driver.text}
                                    </p>
                                  </a>
                                `
                              )}
                            </div>
                          `
                        )}
                      `
                    : html``}
                </div>
              `
            : ''}
          ${this.hasCtas
            ? html`
                <div class="ctas">
                  ${this.ctas.map(cta => {
                    return !!cta.text
                      ? html`
                          <cta-button
                            class="cta-btn"
                            cta-data=${this.ctaData(cta)}
                            cta-type=${cta.buttonType}
                          ></cta-button>
                        `
                      : '';
                  })}
                </div>
              `
            : ''}
        </div>
      </section>
    `;
  }

  get mobileHTML() {
    return html`
      <div class="mobile-panels-wrapper">
        ${this.firstMobilePanelHTML} ${this.secondMobilePanelHTML}
      </div>
    `;
  }

  get firstMobilePanelHTML() {
    return html`
      <section
        class=${classMap({
          slid: this._mobileNavSlid,
          'first-mobile-panel': true,
        })}
      >
        <global-search
          class=${classMap({
            hidden: this._panelShowing,
          })}
        ></global-search>
        <ul class="top-level-items">
          ${this.topLevelNavItems.map(
            navItem =>
              html`
                <li
                  @click="${() => {
                    this.currentpanel = navItem;
                    this._mobileNavSlid = true;
                  }}"
                >
                  ${navItem}
                </li>
              `
          )}
        </ul>
        ${this.mobileFooterHTML}
      </section>
    `;
  }

  get secondMobilePanelHTML() {
    return html`
      <section class="second-mobile-panel">
        ${this.mobileHeaderHTML} ${this.desktopHTML} ${this.mobileFooterHTML}
      </section>
    `;
  }

  get mobileHeaderHTML() {
    return html`
      <div
        class="mobile-section mobile-header"
        @click="${() => {
          this._mobileNavSlid = false;
          this.currentpanel = '';
        }}"
      >
        <img
          src="/static/icons/SVG/right-arrow-8x16.svg"
          class="elements-logo"
        />
        <h2>
          ${this.currentpanel}
        </h2>
      </div>
    `;
  }

  get mobileFooterHTML() {
    return html`
      <div class="mobile-section mobile-footer">
        <ul class="left">
          <li>
            ${isDealerPage()
              ? html`
                  <a
                    @click="${event => {
                      handleAdobeNavEvent({
                        navString: 'NAV|HeaderNav MobileFooter|Home',
                        destinationUrl: '/',
                        event,
                      });
                    }}"
                  >
                    <img
                      src="/static/icons/SVG/3form-logo.svg"
                      class="threeform-logo"
                    />
                  </a>
                `
              : html`
                  <a
                    @click="${event => {
                      handleAdobeNavEvent({
                        navString: 'NAV|HeaderNav MobileFooter|Elements',
                        destinationUrl: '/elements',
                        event,
                      });
                    }}"
                  >
                    <img
                      src="/static/icons/SVG/3form-elements-logo.svg"
                      class="elements-logo"
                    />
                  </a>
                `}
          </li>
          <li>
            <a
              @click="${event => {
                handleAdobeNavEvent({
                  navString: 'NAV|HeaderNav MobileFooter|LightArt',
                  destinationUrl: 'https://lightart.com/',
                  event,
                });
              }}"
            >
              <img
                src="/static/icons/SVG/lightart-logo.svg"
                class="lightart-logo"
              />
            </a>
          </li>
        </ul>
        <ul class="right">
          <li>
            <a href="/order-now">Order Now</a>
          </li>
          <li>
            <a
              href="/request-quote"
              @click="${e => this._footerClickHandler(e, 'Request Quote')}"
            >
              Request a Quote
            </a>
          </li>
          <li>
            <a
              href="#find-rep"
              class="js-find-rep"
              data-micromodal-trigger="modal-find-rep"
              @click="${e => this._footerClickHandler(e, 'Find Rep')}"
            >
              Find a Consultant
            </a>
          </li>
          <li>
            <a
              @click="${e => this._footerClickHandler(e, 'Find an Order')}"
              href="/find-an-order"
            >
              Find an Order
            </a>
          </li>
          <li>
            <a
              @click="${e => this._footerClickHandler(e, 'My3form')}"
              href="/my3form.php/mode/login"
            >
              My3form
            </a>
          </li>
        </ul>
      </div>
    `;
  }

  // SEGMENT METHODS
  get hasSegments() {
    return (
      !!this.secondaryNavItems &&
      !!this.secondaryNavItems.segments &&
      this.secondaryNavItems.segments.length > 0
    );
  }

  get segments() {
    return this.hasSegments ? this.secondaryNavItems.segments : [];
  }

  _findRepClickHandler() {
    document.getElementById('js-toggle-mobile-nav').click();
  }

  _footerClickHandler(event, trigger) {
    let stringBase = trigger === 'Find Rep' ? 'IPN' : 'NAV';
    handleAdobeNavEvent({
      navString: `${stringBase}|HeaderNav MobileFooter|${trigger}`,
      event,
    });

    if (trigger === 'Find Rep' || trigger === 'Request Quote') {
      window.sessionStorage.removeItem('requestInfoData');
    }
  }

  _handleResize = () => {
    const desktopView =
      !window.useMobileLayout && !window.useTabletLayout && !window.isMobile;
    this.style.top = desktopView
      ? `${topNav.getBoundingClientRect().bottom}px`
      : '';

    this.setWindowSizeValues();

    this.requestUpdate();
  };

  segmentHtml(segment) {
    return html`
      <section class="segment-wrapper">
        ${this.segmentLinkHtml(segment)} ${this.segmentDriversHtml(segment)}
      </section>
    `;
  }

  segmentLinkHtml(segment) {
    return html`
      <a
        @click="${event => {
          handleAdobeNavEvent({
            navString: segment.analyticsName,
            destinationUrl: segment.url,
            event,
          });
          window.location.href = segment.url;
        }}"
        ${ref(this.segmentAttributes(segment))}
      >
        ${this.segmentContentHtml(segment)}
      </a>
    `;
  }

  segmentAttributes(segment) {
    // a refers to an <a> HTML element
    return a => {
      if (typeof a === 'undefined') return;

      a.classList.add('segment');

      if (this.segmentHasUrl(segment)) {
        a.href = segment.url;
        if (a.href.includes('reclaim')) {
          a.target = '_blank';
        }
      } else {
        a.removeAttribute('href');
      }
    };
  }

  segmentHasUrl(segment) {
    return !!segment?.url;
  }

  segmentContentHtml(segment) {
    return html`
      <span>
        ${segment.title
          ? html`
              <h3 class="segment-title">
                ${segment.title}
              </h3>
            `
          : html``}
        ${segment.description
          ? html`
              <p class="segment-description">
                ${segment.description}
              </p>
            `
          : html``}
        ${segment.thumbnail_src
          ? html`
              <img src=${this.segmentImgUrl(segment)} />
            `
          : html``}
      </span>
    `;
  }

  segmentImgUrl(segment) {
    return imagebossUrl(segment.thumbnail_src, {
      width: 400,
      height: 400,
    });
  }

  segmentDriversHtml(segment) {
    return this.segmentHasDrivers(segment)
      ? html`
          <div class="segment-drivers">
            ${segment.drivers.map(
              driver => html`
                <a
                  @click="${event => {
                    handleAdobeNavEvent({
                      navString: driver.analyticsName,
                      destinationUrl: driver.link_path,
                      event,
                    });
                  }}"
                  href="${driver.link_path}"
                  class="segment-driver"
                >
                  <p>${driver.name}</p>
                </a>
              `
            )}
          </div>
        `
      : html``;
  }

  segmentHasDrivers(segment) {
    return !!segment.drivers && segment.drivers.length > 0;
  }

  // MISC DRIVERS METHODS
  get hasMiscDrivers() {
    return (
      !!this.secondaryNavItems &&
      !!this.secondaryNavItems.misc_drivers &&
      this.secondaryNavItems.misc_drivers.length > 0
    );
  }

  get miscDrivers() {
    return this.hasMiscDrivers ? this.secondaryNavItems.misc_drivers : [];
  }

  // CTA METHODS
  get hasCtas() {
    return (
      !!this.secondaryNavItems &&
      !!this.secondaryNavItems.ctas &&
      this.secondaryNavItems.ctas.length > 0
    );
  }

  get ctas() {
    return this.hasCtas ? this.secondaryNavItems.ctas : [];
  }

  ctaData(cta) {
    return JSON.stringify({
      text: cta.text,
      url: cta.url,
      analyticsName: cta.analyticsName,
    });
  }

  // HELPER METHODS
  setGridClassName() {
    let newClassName = '';
    const segmentsLength = this.segments.length;
    const classNames = ['', ...gridClassOptions];

    if (segmentsLength <= 5) {
      newClassName = classNames[segmentsLength];
    } else {
      switch (segmentsLength) {
        case 6:
          newClassName = this.hasMiscDrivers
            ? classNames[segmentsLength / 2]
            : classNames[segmentsLength];
          break;
        case 7:
        case 8:
          newClassName = classNames[4];
          break;
        case 9:
        case 10:
          newClassName = classNames[5];
          break;
        default:
          newClassName = classNames[classNames.length - 1];
          break;
      }
    }
    newClassName = this.hasMiscDrivers
      ? `${newClassName}${gridClassMiscDriverSuffix}`
      : newClassName;

    this.resetGridClasses();

    this.gridClasses[newClassName] = true;
  }

  resetGridClasses() {
    gridClassOptions.forEach(gridClassOption => {
      this.gridClasses[gridClassOption] = false;
      this.gridClasses[
        `${gridClassOption}${gridClassMiscDriverSuffix}`
      ] = false;
    });
  }

  get topLevelNavItems() {
    return Object.keys(this.navDataModel);
  }

  get secondaryNavItems() {
    return !!this.currentpanel ? this.navDataModel[this.currentpanel] : {};
  }

  get navDataModel() {
    if (!!this.content) return this.content;

    this.content = JSON.parse(this.getAttribute('content'));

    this.setAnalyticsDataOnContent();

    return this.content;
  }

  setAnalyticsDataOnContent() {
    const navContent = this.content;
    Object.keys(navContent).forEach(topLevelItem => {
      const secondaryNavItems = Object.keys(navContent[topLevelItem]) || [];
      secondaryNavItems.forEach(secondaryItem => {
        const itemObj = navContent[topLevelItem][secondaryItem];
        if (secondaryItem === 'ctas') {
          if (itemObj.length) {
            itemObj.forEach(cta => {
              cta.analyticsName = `NAV|HeaderNav ${topLevelItem}|${cta.text}`;
            });
          }
        } else if (secondaryItem === 'misc_drivers') {
          if (itemObj.length) {
            itemObj.forEach(miscDriver => {
              if (miscDriver.drivers.length) {
                miscDriver.drivers.forEach(driver => {
                  driver.analyticsName = `NAV|HeaderNav ${topLevelItem} ${miscDriver.title}|${driver.text}`;
                });
              }
            });
          }
        } else if (secondaryItem === 'segments') {
          if (itemObj.length) {
            itemObj.forEach(segment => {
              segment.analyticsName = `NAV|HeaderNav ${topLevelItem}|${segment.title}`;
              if (Array.isArray(segment.drivers) && segment.drivers?.length) {
                segment.drivers.forEach(driver => {
                  driver.analyticsName = `Nav|HeaderNav ${topLevelItem}|${segment.title} ${driver.name}`;
                });
              }
            });
          }
        }
      });
    });
  }
}

customElements.define('nav-panel', NavPanel);
